import { lumi } from '@/lib/lumi'
import { useState } from 'react'

interface BlogPost {
  _id: string
  title: string
  subtitle: string
  slug: string
  excerpt: string
  content: string
  tags: string[]
  category: string
  featured: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export const useBlogPosts = () => {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchPosts = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.blog_posts.list({
        sort: { createdAt: -1 },
        ...options
      })
      setPosts(list)
      setTotal(total)
      console.log('Blog posts fetched:', list)
    } catch (error) {
      console.error('Failed to fetch blog posts:', error)
    } finally {
      setLoading(false)
    }
  }

  const createPost = async (postData: Omit<BlogPost, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const newPost = await lumi.entities.blog_posts.create({
        ...postData,
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      })
      console.log('Blog post created:', newPost)
      await fetchPosts()
      return newPost
    } catch (error) {
      console.error('Failed to create blog post:', error)
      throw error
    }
  }

  const updatePost = async (id: string, updates: Partial<BlogPost>) => {
    if (typeof id !== 'string') {
      throw new Error('ID must be a string')
    }

    try {
      const updatedPost = await lumi.entities.blog_posts.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Blog post updated:', updatedPost)
      await fetchPosts()
      return updatedPost
    } catch (error) {
      console.error('Failed to update blog post:', error)
      throw error
    }
  }

  const deletePost = async (id: string) => {
    if (typeof id !== 'string' || id === '[object Object]') {
      throw new Error('Invalid ID: must be a string, not an object')
    }

    try {
      await lumi.entities.blog_posts.delete(id)
      console.log('Blog post deleted:', id)
      await fetchPosts()
    } catch (error) {
      console.error('Failed to delete blog post:', error)
      throw error
    }
  }

  return {
    posts,
    total,
    loading,
    fetchPosts,
    createPost,
    updatePost,
    deletePost
  }
}
